import { defineStore, acceptHMRUpdate } from 'pinia';
import { toast } from '@/composables/useAlertToast';
import { formatDate, nullDateIfNotValid } from '@/utils/date';
import { groupBy } from 'lodash-es';
import { cloneDeep } from 'lodash-es';
import { useUsersStore } from './useUsers';
import { DEFAULT_QUERY, useQueryBuilderStore } from './useQueryBuilder';
import { parseISO } from 'date-fns';
import { strategyApi } from '@/services/strategyService';
import {
  compareGlobalRank,
  isPinnedWorklist,
  insertWorklistAfterLastOfSameType,
  normalizeWorklistRanks,
  normalizeWorkListTypeId,
  worklistNameUniquenessKey,
} from '@/utils/worklistRanking';

export const DEFAULT_STRATEGY_ID = 1;
export const END_OF_DAYS = '9999-12-31T00:00:00';

const getWorklistFingerprint = wl => {
  const assigneeIds = (wl.assignees || [])
    .map(a => a.auxo_user_id ?? a.id)
    .filter(Boolean)
    .sort((a, b) => a - b)
    .join(',');
  const normDate = v => (v ? String(v).slice(0, 10) : '');
  return [
    wl.rank,
    wl.work_list_name ?? '',
    wl.description ?? '',
    wl.owner ?? '',
    wl.owner_id ?? '',
    wl.is_priority ? '1' : '0',
    wl.work_list_type,
    assigneeIds,
    JSON.stringify(wl.criteria_json ?? {}),
    normDate(wl.start_timest),
    normDate(wl.end_timest),
  ].join('|');
};

export const useGamePlanStore = defineStore('gameplans', {
  state: () => ({
    gameplans: [],
    selectedStrategyExecution: {},
    gameplansLoading: false,
    currentGameplanTrigger: 0, //The trigger is to ensure reactivity
    worklists: [],
    filtered_worklists: [],
    previous_worklists: [],
    original_worklists: [],
    worklistsLoading: false,
    execution_info: {},

    draftData: {},
    createDraftLoading: false,
    deleteDraftLoading: false,
    editMode: false,
    on: null, // this is the "on" Date coming in from queryParams

    logoutModal: false,
    timeoutModal: false,

    preview: [],
    previewLoading: false,

    gamePlanRecalculating: false,
    isStrategyLocked: false,
    editingMessage: '',
    editor: '',

    endOfDays: END_OF_DAYS,
    deletedDraftIds: [],
    originalFingerprintsMap: null,
    /** Non-empty => type stats filter active; reorder disabled (STRT-359). */
    worklistTypeFilterIds: [],
  }),
  getters: {
    isCurrentGameplanSelected: state => {
      // This is here to ensure the getter is reactive to changes
      state.currentGameplanTrigger;

      return (
        Object.keys(state.selectedStrategyExecution || {}).length &&
        state.selectedStrategyExecution.is_current
      );
    },
    getEditMode: state => {
      return state.editMode;
    },
    getWorkItemCount: state => {
      return state.selectedStrategyExecution?.total_work_items;
    },
    getTotalBalance: state => {
      return state.selectedStrategyExecution?.total_balance;
    },
    getCurrentExecId: state => {
      return state.execution_info?.execution_id;
    },
    getCurrentExecObject: state => {
      return state.gameplans.find(plan => plan.is_current === true);
    },
    getCurrentGameplan: state => {
      // This is here to ensure the getter is reactive to changes
      state.currentGameplanTrigger;
      let currentGameplan = '';

      if (state.isCurrentGameplanSelected) {
        currentGameplan += 'Current';
      }

      if (Object.keys(state.selectedStrategyExecution || {}).length) {
        return `${currentGameplan ? `${currentGameplan} - ` : ''}${formatDate(
          state.selectedStrategyExecution.execution_date,
          'Pp',
          false
        )}`;
      } else return currentGameplan;
    },
    getGameplans: state => {
      function dateToKey(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      const seenIds = new Set();
      const unique = [];
      for (const g of state.gameplans || []) {
        const rawId = g?.execution_id;
        if (rawId == null || rawId === '') continue;
        const idKey = String(rawId);
        if (seenIds.has(idKey)) continue;
        seenIds.add(idKey);
        unique.push(g);
      }

      return groupBy(unique, gameplan => {
        return dateToKey(parseISO(gameplan.execution_date));
      });
    },
    getSaveState() {
      const sorted = [...this.worklists].sort(compareGlobalRank);
      const work_list_order = sorted.map(el => ({
        work_list_id: el.work_list_id,
        rank: el.rank,
        work_list_type: Number(el.work_list_type) || 5,
      }));

      return {
        work_strategy_draft_id: this.draftData.work_strategy_draft_id,
        work_list_order,
      };
    },
    canReorderWorklists: state => {
      return (
        state.editMode &&
        (!state.worklistTypeFilterIds || state.worklistTypeFilterIds.length === 0)
      );
    },
    // Display counts, balances, preview button
    showMetaData: state => {
      return !state.editMode && !state.gamePlanRecalculating;
    },
    hasGamePlanChanges(state) {
      if ((state.deletedDraftIds?.length ?? 0) > 0) return true;
      if (!state.originalFingerprintsMap) {
        return state.worklists.some(wl => wl.edited);
      }
      if (state.worklists.length !== state.originalFingerprintsMap.size)
        return true;
      for (const wl of state.worklists) {
        const original = state.originalFingerprintsMap.get(wl.work_list_id);
        if (!original) return true;
        if (original !== getWorklistFingerprint(wl)) return true;
      }
      return false;
    },
  },
  actions: {
    setEditMode(editMode) {
      this.editMode = editMode;
      if (!editMode) this.originalFingerprintsMap = null;
    },
    setLockStatusAndMessage(lockStatus, message = '', recalculating = false) {
      this.isStrategyLocked = lockStatus;
      this.editingMessage = message;
      this.gamePlanRecalculating = recalculating;
    },
    async resetWorklists() {
      this.editor = '';
      this.getWorkStrategy(DEFAULT_STRATEGY_ID).then(data => {
        if (data) {
          this.getWorkLists(
            DEFAULT_STRATEGY_ID,
            data.strategy_executions[0].execution_id
          );
          this.selectStrategy(data.strategy_executions[0]);

          const lockStatus = data?.lock_status || {};

          if (Object.keys(lockStatus).length) {
            const { locked, is_calculating, locked_by } = lockStatus;

            if (locked) {
              this.isStrategyLocked = true;
              this.editingMessage = is_calculating
                ? 'The Game Plan strategy is being recalculated. Edit Mode is unavailable.'
                : `${locked_by} is currently editing the Game Plan.`;
            } else {
              this.isStrategyLocked = false;
              this.editingMessage = '';
            }
          }
        }
      });
    },
    async stepBackWorklists() {
      this.worklists = cloneDeep(this.previous_worklists);
    },
    async selectStrategy(strategy) {
      //Triggers the getters to update
      this.currentGameplanTrigger++;
      this.selectedStrategyExecution = { ...strategy };
    },
    setWorklistTypeFilter(typeIds) {
      this.worklistTypeFilterIds = Array.isArray(typeIds) ? [...typeIds] : [];
    },
    toggleWorklistTypeFilter(workListType) {
      const id = Number(workListType);
      if (Number.isNaN(id)) return;
      const cur = [...this.worklistTypeFilterIds];
      const i = cur.indexOf(id);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(id);
      this.worklistTypeFilterIds = cur;
    },
    clearWorklistTypeFilters() {
      this.worklistTypeFilterIds = [];
    },
    async createDraftWorkStrategy() {
      this.createDraftLoading = true;
      try {
        const data = await strategyApi.createDraft(
          this.execution_info.work_strategy_id
        );
        if (!data) return;
        this.draftData = data;
        this.editMode = true;
        this.deletedDraftIds = [];
        return data;
      } finally {
        this.createDraftLoading = false;
      }
    },
    async deleteDraftWorkStrategy() {
      this.deleteDraftLoading = true;
      try {
        const data = await strategyApi.deleteDraft(
          this.execution_info.work_strategy_id,
          this.draftData.work_strategy_draft_id
        );
        if (!data) return;
        this.draftData = data;
        this.editMode = false;
        return data;
      } finally {
        this.deleteDraftLoading = false;
      }
    },
    deleteDraftWorkStrategyOnUnload() {
      const workStrategyId = this.execution_info?.work_strategy_id;
      const draftId = this.draftData?.work_strategy_draft_id;
      if (!draftId || !workStrategyId) return;

      strategyApi.deleteDraftOnUnload(workStrategyId, draftId);
    },
    async getWorkStrategy(strategyId, on) {
      this.gameplansLoading = true;
      try {
        const data = await strategyApi.getStrategy(strategyId, on);
        if (!data) return;

        this.gameplans = data.strategy_executions;
        this.execution_info.work_strategy_id = data.work_strategy_id;
        return data;
      } finally {
        this.gameplansLoading = false;
      }
    },
    async saveWorkStrategy() {
      const gameplan_store = useGamePlanStore();
      const users_store = useUsersStore();

      try {
        const rawPayload = this.getSaveState;

        const { work_strategy_draft_id, work_list_order } = rawPayload;
        const allCurrent = gameplan_store.worklists;

        const deletedIds = this.deletedDraftIds || [];
        const activeLists = allCurrent.filter(
          wl => !deletedIds.includes(wl.work_list_id)
        );

        // Per (trimmed+lower name, work_list_type) — must match validateWorklist, not name alone.
        const seenComboKeys = new Set();
        for (const wl of activeLists) {
          const nKey = worklistNameUniquenessKey(wl.work_list_name);
          if (!nKey) continue;
          const combo = `${nKey}\0${normalizeWorkListTypeId(wl.work_list_type)}`;
          if (seenComboKeys.has(combo)) {
            const label = wl.work_list_name?.trim() || nKey;
            throw new Error(`Worklist name "${label}" already exists.`);
          }
          seenComboKeys.add(combo);
        }

        const expectedCount = activeLists.length;

        const mappedOrder = work_list_order.map(({ work_list_id, rank }) => ({
          work_list_id,
          rank,
        }));
        if (mappedOrder.length !== expectedCount) {
          throw new Error(
            `You must submit ${expectedCount} items; you sent ${mappedOrder.length}.`
          );
        }

        const rankSet = new Set(mappedOrder.map(({ rank }) => rank));
        if (rankSet.size !== mappedOrder.length) {
          throw new Error('Duplicate ranks in work_list_order; ranks must be unique globally.');
        }

        const payload = {
          work_strategy_draft_id,
          work_list_order: mappedOrder,
        };

        const data = await strategyApi.saveStrategy(
          this.execution_info.work_strategy_id,
          payload
        );
        if (!data) return;
        this.resetEditedStatus();
        this.editor = users_store.activeUser.username;
        this.deletedDraftIds = [];
        return data;
      } catch (err) {
        toast.error(err.message);
        console.error(err);
      }
    },

    // Worklist Getter for Gameplan in viewing mode
    async getWorkLists(strategyId, execId, queryTerms) {
      this.worklistsLoading = true;

      this.worklists = [];
      this.previous_worklists = [];
      this.original_worklists = [];

      try {
        let execution_id = execId
          ? execId
          : this.selectedStrategyExecution.execution_id;

        let queryStringParameters = new URLSearchParams();
        if (queryTerms) queryStringParameters.append('filter', queryTerms);

        if (!this.editMode) {
          this.execution_info = {};
        }

        // Always pass execId when known so per-worklist balance/item counts load in edit mode too.
        if (execution_id != null && execution_id !== '') {
          queryStringParameters.append('execId', execution_id);
        }

        const data = await strategyApi.getWorklists(
          strategyId,
          queryStringParameters
        );

        if (!data) return;

        const mappedWorklists = data.work_lists.map(wl => {
          const updatedWorklist = {
            ...wl,
            assignees: wl.assignees?.filter(e => e),
            rank: wl.rank,
            end_timest: nullDateIfNotValid(wl.end_timest),
            edited: false,
          };

          if (updatedWorklist.criteria_json?.rules?.length) {
            updatedWorklist.criteria_json.rules =
              updatedWorklist.criteria_json.rules.map(rule => {
                const dateFields = [
                  'service_end_date',
                  'last_denial_date',
                  'last_claim_date',
                  'last_auxo_activity_dtm',
                ];
                const legacyDateOperators = [
                  'equal',
                  'not_equal',
                  'less_than',
                  'less_than_or_equal',
                  'greater_than',
                  'greater_than_or_equal',
                  'between',
                  'not_between',
                ];

                if (dateFields.includes(rule.field)) {
                  rule.dtype = 'date-or-integer';
                  rule.input = 'dynamic-input';

                  if (legacyDateOperators.includes(rule.operator)) {
                    rule.operator = `${rule.operator}_date`;
                  } else if (rule.operator === 'date_range') {
                    rule.operator = 'greater_than_days';
                  }
                }
                return rule;
              });
          }
          return updatedWorklist;
        });

        const normalizedWorklists = normalizeWorklistRanks(mappedWorklists);
        this.previous_worklists = cloneDeep(normalizedWorklists);
        this.original_worklists = cloneDeep(normalizedWorklists);
        this.worklists = cloneDeep(normalizedWorklists);
        this.worklistTypeFilterIds = [];

        if (this.editMode) {
          this.originalFingerprintsMap = new Map(
            this.worklists.map(wl => [wl.work_list_id, getWorklistFingerprint(wl)])
          );
        }

        if (!this.editMode) {
          this.execution_info = data.execution_info;
          this.execution_info.work_strategy_id = data.work_strategy_id;
        }

        return data;
      } finally {
        this.worklistsLoading = false;
      }
    },

    filterWorklistsLocally(queryTerms) {
      if (!queryTerms) {
        this.filtered_worklists = cloneDeep(this.worklists);
        return;
      }

      const lowerQuery = queryTerms.toLowerCase();
      this.filtered_worklists = this.worklists.filter(
        wl =>
          wl.work_list_name.toLowerCase().includes(lowerQuery) ||
          (wl.description && wl.description.toLowerCase().includes(lowerQuery))
      );
    },
    async editWorklist(worklist, query) {
      const queryBuilder = useQueryBuilderStore();
      if (!queryBuilder.disabledDuringAttempt) return;
      const users_store = useUsersStore();

      const body = {
        change_type: 'edit_work_list',
        payload: {
          work_list_id: worklist.work_list_id,
          work_list_name: worklist.work_list_name,
          description: worklist.description,
          owner: worklist.owner,
          owner_id: worklist.owner_id ?? null,
          start_timest: worklist.start_timest,
          end_timest: nullDateIfNotValid(worklist.end_timest),
          is_priority: worklist.is_priority,
          criteria_json: query ?? {
            ...DEFAULT_QUERY,
            valid: true,
          },
          assignees: worklist.assignees,
          work_list_type: worklist.work_list_type,
        },
      };

      try {
        const data = await strategyApi.submitWorklistChange(
          this.draftData.work_strategy_id,
          this.draftData.work_strategy_draft_id,
          body
        );
        if (!data) return;

        const oldWorklist = this.worklists.find(
          el => el.work_list_id == worklist.work_list_id
        );

        const typeOrPriorityChanged =
          oldWorklist.work_list_type !== worklist.work_list_type ||
          oldWorklist.is_priority !== worklist.is_priority;

        const next = this.worklists.map(el => {
          if (el.work_list_id != worklist.work_list_id) return el;
          return {
            ...el,
            ...data,
            owner: worklist.owner,
            owner_id: worklist.owner_id ?? data.owner_id ?? null,
            mod_by: users_store.activeUser?.username,
            mod_by_user_id: users_store.activeUser?.auxo_user_id,
            mod_timest: new Date(),
            end_timest: nullDateIfNotValid(data.end_timest),
            edited: true,
          };
        });

        if (typeOrPriorityChanged) {
          const updated = next.find(w => w.work_list_id === worklist.work_list_id);
          this.worklists = insertWorklistAfterLastOfSameType(next, updated);
        } else {
          this.worklists = normalizeWorklistRanks(next);
        }

        // Update the state of previous worklists
        this.previous_worklists = cloneDeep(this.worklists);
        return data;
      } finally {
        queryBuilder.setDisabledDuringAttempt(false);
      }
    },
    async saveWorklist(worklist, query) {
      const queryBuilder = useQueryBuilderStore();
      if (!queryBuilder.disabledDuringAttempt) return;
      const users_store = useUsersStore();

      const body = {
        change_type: 'create_work_list',
        payload: {
          work_list_name: worklist.work_list_name,
          description: worklist.description,
          owner: worklist.owner,
          owner_id: worklist.owner_id ?? null,
          start_timest: worklist.start_timest,
          end_timest: worklist.end_timest,
          is_priority: worklist.is_priority,
          criteria_json: query,
          assignees: worklist.assignees,
          work_list_type: worklist.work_list_type,
        },
      };

      try {
        const data = await strategyApi.submitWorklistChange(
          this.draftData.work_strategy_id,
          this.draftData.work_strategy_draft_id,
          body
        );
        if (!data) return;

        const newWl = {
          ...data,
          mod_by: users_store.activeUser?.username,
          mod_by_user_id: users_store.activeUser?.auxo_user_id,
          mod_timest: new Date(),
          end_timest: nullDateIfNotValid(data.end_timest),
          edited: true,
        };
        this.worklists = insertWorklistAfterLastOfSameType(this.worklists, newWl);

        // Update the state of previous worklists
        this.previous_worklists = cloneDeep(this.worklists);
        return data;
      } finally {
        queryBuilder.setDisabledDuringAttempt(false);
      }
    },
    async deleteWorklist(worklist) {
      const body = {
        change_type: 'delete_work_list',
        payload: {
          work_list_id: worklist.work_list_id,
        },
      };

      const data = await strategyApi.submitWorklistChange(
        this.draftData.work_strategy_id,
        this.draftData.work_strategy_draft_id,
        body
      );
      if (!data) return;

      this.deletedDraftIds.push(worklist.work_list_id);

      this.worklists = normalizeWorklistRanks(
        this.worklists.filter(w => w.work_list_id !== worklist.work_list_id)
      );

      this.previous_worklists = cloneDeep(this.worklists);
      return data;
    },
    async getWorkItemPreview(strategyId = DEFAULT_STRATEGY_ID, workListId) {
      this.previewLoading = true;
      this.preview = [];

      try {
        const data = await strategyApi.previewWorklist(strategyId, workListId);
        if (!data) return;
        this.preview = data;
        return data;
      } finally {
        this.previewLoading = false;
      }
    },
    resetEditedStatus(boolean = false) {
      const mappedWorklists = this.worklists.map(wl => {
        return {
          ...wl,
          edited: boolean,
        };
      });
      this.worklists = cloneDeep(mappedWorklists);
    },
    async testCriteria(query) {
      const data = await strategyApi.testCriteria(query);
      return data;
    },
    showFailToast() {
      const users_store = useUsersStore();
      if (this.editor == users_store.activeUser.username)
        toast.error('The game plan calculation failed');
    },

    updateWorklistRank(targetWorklist, newRank) {
      if (!this.canReorderWorklists) return false;
      if (isPinnedWorklist(targetWorklist)) return false;

      const movable = this.worklists.filter(w => !isPinnedWorklist(w));
      if (newRank < 1 || newRank > movable.length) {
        return false;
      }

      const sortedMovable = [...movable].sort(compareGlobalRank);
      const fromIdx = sortedMovable.findIndex(
        w => w.work_list_id === targetWorklist.work_list_id
      );
      if (fromIdx === -1) return false;

      const currentRank = fromIdx + 1;
      if (currentRank === newRank) {
        return true;
      }

      const [item] = sortedMovable.splice(fromIdx, 1);
      sortedMovable.splice(newRank - 1, 0, item);

      let r = 1;
      const idToRank = new Map();
      for (const w of sortedMovable) idToRank.set(w.work_list_id, r++);
      for (const w of this.worklists.filter(isPinnedWorklist).sort(compareGlobalRank)) {
        idToRank.set(w.work_list_id, r++);
      }

      this.worklists = this.worklists.map(w => {
        const newRankVal = idToRank.get(w.work_list_id) ?? w.rank;
        const rankChanged = newRankVal !== w.rank;
        return {
          ...w,
          rank: newRankVal,
          edited: w.edited || rankChanged,
        };
      });

      this.previous_worklists = cloneDeep(this.worklists);
      return true;
    },

    /** Apply ranks from tbody row order after handle-only @dnd-kit sortable drag. */
    reorderWorklistsFromDomOrder(orderedWorklistIds) {
      if (!this.canReorderWorklists) return false;
      if (!orderedWorklistIds?.length) return false;

      let r = 1;
      const idToRank = new Map();
      for (const raw of orderedWorklistIds) {
        const id = Number(raw);
        if (!Number.isFinite(id)) continue;
        idToRank.set(id, r++);
      }

      this.worklists = this.worklists.map(w => {
        const newRankVal = idToRank.get(Number(w.work_list_id)) ?? w.rank;
        const rankChanged = newRankVal !== w.rank;
        return {
          ...w,
          rank: newRankVal,
          edited: w.edited || rankChanged,
        };
      });
      this.worklists = normalizeWorklistRanks(this.worklists);
      this.previous_worklists = cloneDeep(this.worklists);
      return true;
    },
    async unlockGameplan() {
      //! FOR DEV LOCAL & ENG/TEST USE ONLY
      const data = await strategyApi.unlockGameplan();
      if (!data) return;
      this.setLockStatusAndMessage(false);
      return data;
    },
    async calcWorkStrat() {
      //! FOR DEV LOCAL USE ONLY
      const data = await strategyApi.calcWorkStrat();
      if (!data) return;
      toast.success('Work Strategy Calculated. Please refresh the page.');
      return data;
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGamePlanStore, import.meta.hot));
}
